import React, { useState } from 'react';
import { TitleText, BodyText, BodyTextSmall } from './common';
import { ContinueWithAppleButton } from '../components/ContinueWithAppleButton';
import { ContinueWithEmailButton } from '../components/ContinueWithEmailButton';
import { ContinueWithFacebookButton } from '../components/ContinueWithFacebookButton';
import { ContinueWithGoogleButton } from '../components/ContinueWithGoogleButton';
import { YourPaperCard } from '../components/YourPaperCard';
import routes from 'js/core_pre_webpack/routes';
import {
  Options as ClickTrackingOptions,
  trackClick,
} from 'js/utils/trackClick';
import styled, { stylesheet } from 'astroturf/react';
import { VerticalSpacer } from '../components/VerticalSpacer';
import { ModalError } from '../components/ModalError';
import {
  ButtonsPageConfigurableText,
  RegistrationParams,
  ModalPage,
  HeaderVariant,
  CtaLayout,
} from '../types';

type Props = {
  onAttempt?: () => void;
  onAuthorized?: () => void;
  onSuccess?: (redirect?: string) => void;
  onError: (error?: { errorMessage?: string; errorCodes?: string[] }) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  errorMessage?: string;
  onEmailButtonClick: () => void;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: ButtonsPageConfigurableText;
  registrationParams: RegistrationParams;
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
  // Structural sockets (default to today's behavior).
  headerVariant?: HeaderVariant;
  ctaLayout?: CtaLayout;
  // Work context, read only by headerVariant: 'yourPaperCard'.
  workTitle?: string;
  authorName?: string;
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
};

const ButtonStyleContext = styled.div<{ show: boolean }>`
  display: none;
  flex-direction: column;

  &.show {
    display: flex;
  }
`;

const SharedButtonStyles = stylesheet`
  .margins {
    margin-top: 8px;
    margin-bottom: 8px;
    position: relative; // so the icons can be absolutely positioned
  }

  .margins img {
    position: absolute;
    left: 24px;
    bottom: 20px;
    height: 24px;
  }
`;

const ExpanderButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  margin: 8px auto;
  cursor: pointer;
  color: var(--text-primary, #141413);
  font-size: 14px;
  text-decoration: underline;
  font-family: inherit;

  &:hover {
    opacity: 0.8;
  }
`;

export const ButtonsPage: React.FC<Props> = (props) => {
  const {
    onAttempt,
    onSuccess,
    onError,
    loading,
    setLoading,
    errorMessage,
    onEmailButtonClick,
    show,
    clickTrackingContext,
    text,
    registrationParams,
    setPage,
    headerVariant = 'default',
    ctaLayout = 'all',
    workTitle,
    authorName,
    workThumbnailUrl,
    workYear,
    workViewCount,
  } = props;

  const [expanded, setExpanded] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userInChina = (window as any).userInChina === 'true';

  // Feature flag for disabling Facebook login - hardcoded to true for testing
  const facebookLoginDisabled =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).facebookLoginDisabled === 'true';

  const handleFacebookButtonClick = () => {
    if (facebookLoginDisabled) {
      // Navigate to the facebook-disabled page
      setPage('facebook-disabled');
    }
    // If not disabled, the ContinueWithFacebookButton handles its own click logic
  };

  const handleExpanderClick = () => {
    setExpanded(true);
    // Spread context first so `source` is not overridden by an opener source.
    trackClick({
      ...clickTrackingContext,
      source: 'login_modal_expander_open',
    });
  };

  const googleButton = !userInChina && (
    <ContinueWithGoogleButton
      onAttempt={onAttempt}
      onError={onError}
      onSuccess={onSuccess}
      disabled={loading}
      className={SharedButtonStyles.margins}
      setLoading={setLoading}
      clickTrackingContext={clickTrackingContext}
      buttonText={text.googleButton}
      registrationParams={registrationParams}
    />
  );

  const appleButton = (
    <ContinueWithAppleButton
      onAttempt={onAttempt}
      onError={onError}
      onSuccess={onSuccess}
      disabled={loading}
      className={SharedButtonStyles.margins}
      setLoading={setLoading}
      clickTrackingContext={clickTrackingContext}
      buttonText={text.appleButton}
      registrationParams={registrationParams}
    />
  );

  const facebookButton =
    !userInChina &&
    (facebookLoginDisabled ? (
      // When Facebook login is disabled, render a button that looks the same but navigates to the disabled page
      <ContinueWithFacebookButton
        onClick={handleFacebookButtonClick}
        onError={onError}
        onSuccess={onSuccess}
        disabled={loading}
        className={SharedButtonStyles.margins}
        setLoading={setLoading}
        clickTrackingContext={clickTrackingContext}
        buttonText={text.facebookButton}
        registrationParams={registrationParams}
      />
    ) : (
      // Normal Facebook OAuth flow
      <ContinueWithFacebookButton
        onAttempt={onAttempt}
        onError={onError}
        onSuccess={onSuccess}
        disabled={loading}
        className={SharedButtonStyles.margins}
        setLoading={setLoading}
        clickTrackingContext={clickTrackingContext}
        buttonText={text.facebookButton}
        registrationParams={registrationParams}
      />
    ));

  const emailButton = (
    <ContinueWithEmailButton
      onClick={onEmailButtonClick}
      disabled={loading}
      className={SharedButtonStyles.margins}
      buttonText={text.emailButton}
      userInChina={userInChina}
    />
  );

  return (
    <ButtonStyleContext show={show}>
      {headerVariant === 'yourPaperCard' ? (
        <>
          <YourPaperCard
            paperTitle={workTitle?.trim() || 'this paper'}
            authorName={authorName}
            thumbnailUrl={workThumbnailUrl}
            year={workYear}
            viewCount={workViewCount}
          />
          {/* Opt-in subheader (e.g. speed lever), below the card. */}
          {text.subheader && (
            <>
              <VerticalSpacer height={12} />
              <BodyText>{text.subheader}</BodyText>
            </>
          )}
        </>
      ) : (
        <>
          <TitleText>{text.header}</TitleText>
          <BodyText>{text.subheader}</BodyText>
        </>
      )}
      <ModalError errorMessage={errorMessage} show={Boolean(errorMessage)} />
      <VerticalSpacer height={headerVariant === 'yourPaperCard' ? 24 : 32} />
      <BodyTextSmall>
        By clicking {text.disclaimerVerb}, you agree to our{' '}
        <a href={routes.terms_path()}>Terms of Use</a> and{' '}
        <a href={routes.privacy_path()}>Privacy Policy</a>
      </BodyTextSmall>
      <VerticalSpacer height={16} />
      {ctaLayout === 'googleOnlyExpander' ? (
        <>
          {googleButton}
          {!expanded && (
            <>
              <VerticalSpacer height={8} />
              <ExpanderButton type="button" onClick={handleExpanderClick}>
                {text.expanderLabel}
              </ExpanderButton>
            </>
          )}
          {expanded && (
            <>
              {appleButton}
              {facebookButton}
              {emailButton}
            </>
          )}
        </>
      ) : (
        <>
          {googleButton}
          {appleButton}
          {facebookButton}
          {emailButton}
        </>
      )}
    </ButtonStyleContext>
  );
};
